import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useToast } from '~/lib/ui/toast'
import { cn } from '~/lib/utils'
import { apiPost } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { Textarea } from '~/lib/ui/textarea'
import { useCandidate, queryClient } from './CandidateProvider'
import { Label } from '~/lib/ui/label'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useResumeReview } from './ResumeReviewDetail'
import { useServiceRequests } from './ServicesSection'
import PrivacySettings from './PrivacySettings'
import YnsIcon from '../icons/YnsIcon'

export const DurationSelect = ({ onValueChange }) => {
  const durationOptions = [15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(false)
  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }
  return (
    <div className="flex flex-row justify-evenly">
      <Select onValueChange={handleValueChange} className="w-full">
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className="ml-2 w-full"
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
        />
      )}
    </div>
  )
}

const UploadResumeRevision = ({ revisionNumber, children }) => {
  const {
    candidate: { id: candidateId },
    coachId,
    detailItemId,
  } = useCandidate()
  const [open, setOpen] = useState(false)
  const [resumeFile, setResumeFile] = useState({})
  const [noteDetails, setNoteDetails] = useState('')
  const [privacySettings, setPrivacySettings] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const onFileSelected = (file) => {
    setResumeFile(file)
  }
  const [files, setFiles] = useState([])
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings.indexOf('all') > -1 ? allPrivacySettings : privacySettings
  const [duration, setDuration] = useState('')
  const { toast } = useToast()

  const postResumeRevision = async (formData) => {
    setIsSaving(true)
    const postPath = `/coach/candidates/${candidateId}/resume_revisions.json`
    const response = await apiPost(postPath, formData, true)
    return response
  }

  const createRevisionMutation = useMutation({
    mutationFn: postResumeRevision,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      toast({
        title: 'Success',
        description: 'Resume revision created successfully',
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error('Error creating resume revision:', error)
      const description =
        error?.response?.data?.error ||
        'An error occurred while uploading the resume.'
      console.log('Error description:', description)
      toast({
        description,
        title: 'Could Not Upload Resume',
        variant: 'destructive',
        duration: 10_000,
      })
    },
  })

  const saveClick = async () => {
    if (isSaving) return null

    const f = new FormData()
    f.append('resume_revision_coach_revision[candidate_id]', candidateId)
    f.append('resume_revision_coach_revision[coach_id]', coachId)
    f.append('resume_revision_coach_revision[resume_review_id]', detailItemId)
    f.append('resume_revision_coach_revision[notes]', noteToCandidate)
    if (revisionNumber && revisionNumber == 2) {
      f.append('resume_revision_coach_revision[close_review]', 1)
    }

    f.append(
      'resume_revision_coach_revision[coach_note_attributes][duration]',
      duration,
    )
    f.append(
      'resume_revision_coach_revision[coach_note_attributes][topics][Resume][]',
      'Edit/Review',
    )
    formPrivacySettings.forEach((setting) => {
      f.append(
        'resume_revision_coach_revision[coach_note_attributes][privacy_settings][]',
        setting,
      )
    })
    f.append(
      'resume_revision_coach_revision[coach_note_attributes][note]',
      noteDetails,
    )
    f.append('resume_revision_coach_revision[remote_content]', resumeFile)

    files.forEach((file, idx) => {
      f.append(
        `resume_revision_coach_revision[service_request_revisions_documents_attributes][${idx}][uploaded_document]`,
        file,
      )
    })
    createRevisionMutation.mutate(f)
  }

  const resumeReview = useResumeReview()
  const { meetingViaCalendly } = resumeReview.candidateRevisions?.[0] || false

  const { serviceRequests } = useServiceRequests()
  const currentResumeReview = serviceRequests?.find(
    (serviceRequest) => serviceRequest.id == detailItemId,
  )

  function getNoteDefaults(
    meetingViaCalendly,
    revisionNumber,
    coachName,
    candidateName,
    currentResumeReview,
  ) {
    const prefix = candidateName ? `Hi ${candidateName},\n\n` : ''
    const suffix = coachName ? `\n\nBest regards,\n${coachName}` : ''

    if (
      meetingViaCalendly === undefined &&
      !currentResumeReview.revisionDeclined
    ) {
      if (currentResumeReview.meetingViaCalendly === true) {
        return (
          prefix +
          'It was great speaking with you today [COACH TO EDIT OR DELETE (or insert date you spoke])! As we discussed, I’m attaching your resume with the suggested changes for your review. Once you make the edits, you can either request another meeting to review the revised document or I can review it electronically.\n\nTo continue, simply log back into Your Next Step and return to the Resume Review page to take the next step.\n\n {view_resume_review_button} \n\nI look forward to hearing from you again soon!' +
          suffix
        )
      } else {
        return (
          prefix +
          'Thank you for submitting your resume review! Overall, you did a great job with [COACH TO EDIT (state a few things they did well]). \n\nThere are a few areas that would benefit from some revisions. I have attached a version of your resume with comments and edits for you to review.\n\nOnce you have had a chance to review my suggestions and make some updates, please return to Your Next Step and visit the Resume Review page to continue.\n\n {view_resume_review_button} \n\nI look forward to continuing to support you on your resume development.' +
          suffix
        )
      }
    } else if (meetingViaCalendly) {
      if (
        revisionNumber == 2 ||
        (revisionNumber == 1 && currentResumeReview?.revisionDeclined)
      ) {
        return (
          prefix +
          'It has been a pleasure working with you to revise your resume. [COACH TO EDIT( Coach to provide a few sentences highlighting what was discussed)].\n\nI’m attaching a final clean resume document as well as a checklist to guide you in future revisions as you tailor for specific opportunities. Congratulations on taking this step and making progress toward your goal!\n\nWhen you return to your Resume Review page in Your Next Step you will see an option to share feedback on your experience. Your satisfaction is very important to us and I would appreciate hearing any comments you would like to share.\n\n {view_resume_review_button} \n\nWhile this concludes our interactions, if you need any additional coach support please use the Coach Chat button available throughout Your Next Step. Our professional coaches are available to assist via text, video, or audio chat to answer questions on job search strategy, interview preparation, networking and more. I wish you the best of luck as you continue your search!' +
          suffix
        )
      } else {
        return (
          prefix +
          'It was nice speaking with you today! [COACH TO EDIT (Coach to provide a few sentences highlighting what was discussed as part of the second revision]).\n\nPlease review these notes as you prepare your final resume. When you return to the Resume Review page please look for the opportunity to provide feedback on your resume review experience.\n\n {view_resume_review_button} \n\nAs you are nearing the end of your resume review process, I want to encourage you to take advantage of the Coach Chat service for additional support on your resume, cover letters, interview preparation and more.\n\nI wish you the best of luck as you go to market with your refreshed resume!' +
          suffix
        )
      }
    } else {
      if (
        revisionNumber == 2 ||
        (revisionNumber == 1 && currentResumeReview?.revisionDeclined)
      ) {
        return (
          prefix +
          'I hope you are doing well. It has been a pleasure working with you to revise your resume.[COACH TO EDIT provide some personal note or state something positive about their resume outcome]\n\nI’m attaching a final clean resume document as well as a checklist to guide you in future revisions as you tailor for specific opportunities. Congratulations on taking this step and making progress toward your goal!\n\nWhen you return to your Resume Review page in Your Next Step you will see an option to share feedback on your experience. Your satisfaction is very important to us and I would appreciate hearing any comments you would like to share. \n\n {view_resume_review_button} \n\nWhile this concludes our interactions, if you need any additional coach support please use the Coach Chat button available throughout Your Next Step. Our professional coaches are available to assist via text, video, or audio chat to answer questions on job search strategy, interview preparation, networking and more. I wish you the best of luck as you continue your search!' +
          suffix
        )
      } else {
        return (
          prefix +
          'Thanks for sending your edits! Overall, you did a great job with [COACH TO EDIT (coach to list positive feedback]).\n\nI have a couple of final suggestions for you. [COACH TO EDIT (coach to list a few recommendations]).\n\nI’m attaching an updated version of your resume with these suggestions for your review. Please consider including these in the final version of your resume. When you return to the Resume Review page please look for the opportunity to provide feedback on your resume review experience.\n\n {view_resume_review_button} \n\nAs you are nearing the end of your resume review process, I want to encourage you to take advantage of the Coach Chat service for additional support on your resume, cover letters, interview preparation and more.\n\nI wish you the best of luck as you go to market with your refreshed resume!' +
          suffix
        )
      }
    }
  }

  const noteDefaults = getNoteDefaults(
    meetingViaCalendly,
    revisionNumber,
    resumeReview.coachName,
    resumeReview.candidateName,
    currentResumeReview,
  )

  const [noteToCandidate, setNoteToCandidate] = useState(noteDefaults)

  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">New Resume Revision</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[80vh] overflow-y-auto px-4 pb-4">
          <DialogDescription className="p-0 text-lg">
            Create a new Resume Revision for this candidate.
          </DialogDescription>
          <div className="py-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes)
            </Label>
            <DurationSelect onValueChange={setDuration} />
          </div>
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Internal System Admin Notes
            </Label>
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
            <Textarea
              className="min-h-56 w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <hr className="border-t-2 border-gray-300" />
          <div className="py-4">
            <Label htmlFor="noteToCandidate" className="text-lg !font-medium">
              Please record any remarks with this update. Comments will be
              viewed by the candidate.
            </Label>
            <Textarea
              className="min-h-56 w-full"
              name="noteToCandidate"
              value={noteToCandidate}
              onChange={(e) => setNoteToCandidate(e.target.value)}
            />
          </div>
          <div>
            <Dropzone onFileSelected={onFileSelected}>
              {resumeFile.name ? (
                <>
                  <p className="mb-4">
                    <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
                    {`${resumeFile.name} (${(resumeFile.size / 1024).toFixed(2)} KB) ${resumeFile.type ? ` (${resumeFile.type})` : ''}`}
                  </p>
                  <p>
                    Drag and drop a new file to replace the current selection.
                  </p>
                </>
              ) : (
                <p>
                  Drag and drop the resume revision here, or click to select
                </p>
              )}
            </Dropzone>

            <AddMoreFiles setFiles={setFiles} />
          </div>
          <div className="flex flex-row justify-end">
            <Button
              className="inline"
              onClick={saveClick}
              disabled={
                isSaving ||
                !resumeFile.name ||
                noteToCandidate.toUpperCase().includes('COACH TO EDIT')
              }
            >
              Save
            </Button>
          </div>
          <p className="mt-2 flex flex-row justify-end text-red-500">
            {noteToCandidate.toUpperCase().includes('COACH TO EDIT') ? (
              <span>
                <b>Save Disabled:</b> Address the &quot;COACH TO EDIT&quot; text
                in your note to the candidate.
              </span>
            ) : !resumeFile.name ? (
              <span>
                <b>Save Disabled:</b> Please select a Resume Revision File.
              </span>
            ) : (
              ''
            )}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default UploadResumeRevision

const Dropzone = ({ onFileSelected = () => null, className, children }) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const { toast } = useToast()

  const onDropAccepted = (incomingFiles) => {
    const incomingFile = incomingFiles[0]
    onFileSelected(incomingFile)
  }

  const onDropRejected = (incomingFiles) => {
    const {
      file: { type },
      errors,
    } = incomingFiles[0]
    const description = errors.map((e) => ` • ${e.message}`).join('\n')
    console.error('File upload rejected:', description)
    if (type) {
      toast({
        description,
        title: 'Could Not Upload Resume',
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
    },
    onDropAccepted,
    onDropRejected,
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: () => setIsDraggingOver(false),
    maxSize: 15_000_000, // 15MB
  })

  const baseRootClassName = cn(
    'min-h-[10rem] p-4 border-dashed border text-center content-center justify-items-center cursor-pointer',
    className,
  )
  const rootClassName = isDraggingOver
    ? baseRootClassName + ' border-primary border-2 bg-lily-blue'
    : baseRootClassName

  return (
    <div className="pb-4">
      <div className="mb-2 flex flex-row items-center justify-start">
        <div className="text-lg font-medium">
          Resume Revision File <em>(required)</em>
        </div>
      </div>
      <div
        {...getRootProps({
          className: rootClassName,
        })}
      >
        <input {...getInputProps()} />
        <div>
          {children}
          <em>(Files must be in .pdf, .doc, or .docx format)</em>
        </div>
      </div>
    </div>
  )
}

const AddMoreFiles = ({ setFiles }) => {
  const [dropzones, setDropzones] = useState([])
  const handleAddDropzone = () => {
    const lastVal = dropzones[dropzones.length - 1]
    setDropzones((prev) => [...prev, lastVal + 1])
  }

  return (
    <div className="mt-4">
      <div className="grid grid-cols-2 gap-4">
        {dropzones.map((_, idx) => (
          <DropzoneItem
            key={`dropzone-${idx}`}
            setDropzones={setDropzones}
            setFiles={setFiles}
            index={idx}
          />
        ))}
      </div>
      <Button variant="link" onClick={handleAddDropzone} className="mt-2">
        + Add file
      </Button>
    </div>
  )
}

const DropzoneItem = ({ setFiles, index }) => {
  const handleRemoveDropzone = () => {
    const dropzoneElement = document.getElementById(`dropzone-${index}`)
    if (dropzoneElement) {
      dropzoneElement.remove()
    }
    setFiles((prevFiles) => prevFiles.filter((_, idx) => idx !== index))
  }
  const [otherFile, setOtherFile] = useState({})

  const onFileSelected = (file) => {
    setOtherFile(file)
    const dropzones = document.querySelectorAll('.dropzone-item')
    if (dropzones.length > 0) {
      const newFiles = Array.from(dropzones).map(
        (el) => el.querySelector('input[type="file"]').files[0],
      )
      setFiles(newFiles)
    }
  }
  return (
    <div
      id={`dropzone-${index}`}
      className="dropzone-item flex items-center gap-2"
    >
      <Dropzone onFileSelected={onFileSelected}>
        {otherFile.name ? (
          <>
            <p className="mb-4">
              <CheckCircleIcon className="mr-2 inline w-6 text-intoo-green-coachcta" />
              {`${otherFile.name} (${(otherFile.size / 1024).toFixed(2)} KB) ${otherFile.type ? ` (${otherFile.type})` : ''}`}
            </p>
            <p>Drag and drop a new file to replace the current selection.</p>
          </>
        ) : (
          <p>Drag and drop a resume revision here, or click to select</p>
        )}
      </Dropzone>
      <Button onClick={handleRemoveDropzone} variant="ghost">
        <YnsIcon name="trash" className="h-6 w-6" />
      </Button>
    </div>
  )
}
